import React, { FormEvent, useEffect, useState } from 'react';

import { PatternFormat } from 'react-number-format';
import styled from 'styled-components';

import RadioOffSVG from 'assets/radio-off.inline.svg';
import RadioOnSVG from 'assets/radio-on.inline.svg';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import { ContactFormI, ContactFormSourceType } from 'interfaces/content-stack/modular-blocks/contactForm';

import { HttpClient } from 'services/http';

import { dataLayerPush } from 'utils/functions';

export const FORM_ENDPOINT =
  process.env.GATSBY_SALESFORCE_ENDPOINT || 'https://www.ig.com/productapplicationsgateway/leadcapture/prospectsource';
const MT4_CAMPAIGN = '7012X000001k1EW';
const NEWSLETTER_CAMPAIGN = '7016N000000lJOxQAM';
const PREMIUM_SERVICES_CAMPAIGN = '701P600000JgjVeIAJ';

const SignupHeader = styled.h3`
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
`;

const Description = styled.div`
  margin: 2rem 0;
  font-size: 1.25rem;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
  font-family: ${(props) => props.theme.fonts.default};

  a {
    color: inherit;
    transition: 0.3s;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  input[type='radio'] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    clip: rect(0, 0, 0, 0);
    overflow: hidden;
    white-space: nowrap;
  }
  input[type='radio']:focus-visible + label {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlack};
    outline-offset: 4px;
  }
`;

const CheckLabel = styled.label`
  display: flex;
  flex-direction: row;
  position: relative;
  cursor: pointer;
`;

const StyledTextInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.colors.gray};
  transition: border 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  outline: none;

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlack};
  }
`;

const StyledFieldset = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    gap: 1rem;
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  width: 100%;
  position: relative;
`;

const LabelAndInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  div {
    display: none !important;
  }
`;

const StyledLabel = styled.label`
  font-weight: 600;
`;

const SuccessMessage = styled.div`
  font-family: ${(props) => props.theme.fonts.default};
  color: ${(props) => props.theme.colors.cleanGreen};
  font-weight: 500;
  font-size: 1.25rem;
  margin-top: 1.5rem;
`;

const ErrorMessage = styled.div`
  font-family: ${(props) => props.theme.fonts.default};
  color: ${(props) => props.theme.colors.ttRed};
  font-weight: 500;
  font-size: 1.25rem;
  margin-top: 1.5rem;
`;

const FailureMessage = styled.p`
  font-family: ${(props) => props.theme.fonts.default};
  color: ${(props) => props.theme.colors.ttRed};
  font-weight: 500;
  margin: 0;
`;

const SubmitButton = styled.input`
  border: 0.0625rem solid ${(props) => props.theme.colors.ttRed};
  background-color: ${(props) => props.theme.colors.ttRed};
  text-decoration: none;

  color: ${(props) => props.theme.colors.white};
  font-family: ${(props) => props.theme.fonts.default};
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  border-radius: 5rem;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;

  &:hover {
    background-color: ${(props) => props.theme.colors.darkerRed};
    border-color: ${(props) => props.theme.colors.darkerRed};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: 100%;
  }
`;

const SubmitButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SendingFormMessage = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 1.25rem;
`;

const Legend = styled.legend`
  float: unset;
  font-size: 1rem;
  font-weight: 400;
`;

const RadioOn = styled(RadioOnSVG)`
  color: ${(props) => props.theme.colors.ttRed};
`;

const RadioOff = styled(RadioOffSVG)`
  color: ${(props) => props.theme.colors.gray};
`;

export interface ContactFormProps {
  className: string;
  data: ContactFormI;
}

const SOURCE_TYPE_CAMPAIGN_ID_MAPPING = {
  [ContactFormSourceType.MT4_DEMO]: MT4_CAMPAIGN,
  [ContactFormSourceType.NEWSLETTER]: NEWSLETTER_CAMPAIGN,
  [ContactFormSourceType.PREMIUM_SERVICES]: PREMIUM_SERVICES_CAMPAIGN,
} as const;

function ContactForm({ data, className }: ContactFormProps) {
  const {
    description,
    header,
    sourceType,
    usePhoneNumber,
    phoneRequired,
    privacyNoticeAboveMainCTA,
    submitButtonText,
    confirmationMessage,
    phoneFieldHint,
  } = data;
  const campaignId = SOURCE_TYPE_CAMPAIGN_ID_MAPPING[sourceType];
  const successConfirmationMessage = confirmationMessage || 'Sign up successful - thank you';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);

  const [firstNameValid, setFirstNameValid] = useState<boolean | null>(null);
  const [lastNameValid, setLastNameValid] = useState<boolean | null>(null);
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [phoneValid, setPhoneValid] = useState<boolean | null>(null);

  const firstNameRegex = /^(?=.{1,50}$)[a-z]+(?:['-\s][a-z]+)*$/im;
  const lastNameRegex = /^(?=.{2,50}$)[a-z]+(?:['-\s][a-z]+)*$/im;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]+$/;

  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingForm, setSendingForm] = useState(false);
  const [eventFired, setEventFired] = useState(false);

  const validateFirstName = () => {
    setFirstNameValid(firstNameRegex.test(firstName));
  };

  const validateLastName = () => {
    const result = lastNameRegex.test(lastName);
    setLastNameValid(result);
  };

  const validateEmail = () => {
    setEmailValid(emailRegex.test(email));
  };

  const validatePhone = () => {
    if (phoneRequired) {
      setPhoneValid(phoneRegex.test(phone));
      return;
    }
    setPhoneValid(phone === '' || phoneRegex.test(phone));
  };

  const isInvalid = (value: boolean | null) => {
    return value === false;
  };

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    validateFirstName();
    validateLastName();
    validateEmail();

    if (usePhoneNumber) {
      validatePhone();
    } else {
      setPhoneValid(true);
    }

    if (!(firstNameValid && lastNameValid && emailValid && phoneValid) || success) {
      return;
    }

    const formData = {
      marketingPreferencesOptIn: emailConsent,
      sourceType: sourceType,
      locale: 'en_US',
      managingOffice: 'USA',
      siteId: 'usa',
      campaignId: campaignId,
      prospectClient: {
        forename: firstName,
        surname: lastName,
        email: email,
        telephoneNumber: phone || null,
      },
    };

    const jsonData = JSON.stringify(formData);

    try {
      setSendingForm(true);
      const response = await HttpClient.post(FORM_ENDPOINT, jsonData, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      await response.text();

      setSuccess(true);
      setMessage(successConfirmationMessage);
    } catch (fetchError) {
      setMessage(`Server error - please try again`);
      setSuccess(false);
    } finally {
      setSendingForm(false);
    }
  };

  const PhoneTextProps = {
    id: 'phone',
    name: 'phone',
    value: phone,
    onBlur: validatePhone,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value.replace(/[\s()-]+/g, '')),
  };

  const handleStartForm = () => {
    if (eventFired) return;
    dataLayerPush('lead_capture_form_interaction', 'event', {
      error: undefined,
      event: 'lead_capture_form_interaction',
      event_action: 'start form',
      event_category: 'lead capture',
      event_label: `US ${sourceType}`,
      event_value: undefined,
      field_name: undefined,
      interaction_type: 'start form',
      interaction_value: `US ${sourceType}`,
    });
    setEventFired(true);
  };

  useEffect(() => {
    if (success) {
      dataLayerPush('lead_capture_form_signup', 'event', {
        error: undefined,
        event: 'lead_capture_form_signup',
        event_action: 'Signup',
        event_category: 'lead capture',
        event_label: `US ${sourceType}`,
        event_value: undefined,
        field_name: undefined,
        interaction_type: 'Signup',
        interaction_value: `US ${sourceType}`,
      });
    }
  }, [success]);

  return (
    <ContentContainer className={className}>
      <SignupHeader>{header}</SignupHeader>
      {description && (
        <Description>
          <InnerHtml richText={description} />
        </Description>
      )}
      <StyledForm onSubmit={submitForm} noValidate={true}>
        <StyledFieldset>
          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='firstName'>First name</StyledLabel>
              <StyledTextInput
                required
                type='text'
                id='firstName'
                data-testid='firstName'
                name='forename'
                aria-invalid={isInvalid(firstNameValid)}
                aria-describedby={isInvalid(firstNameValid) ? 'invalidFirstName' : undefined}
                value={firstName}
                onBlur={validateFirstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  handleStartForm();
                }}
              />
              {isInvalid(firstNameValid) && (
                <FailureMessage id='invalidFirstName'>Please enter a valid first name</FailureMessage>
              )}
            </LabelAndInputContainer>
          </FormGroup>

          <FormGroup>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='lastName'>Last name</StyledLabel>
              <StyledTextInput
                required
                type='text'
                id='lastName'
                data-testid='lastName'
                name='surname'
                value={lastName}
                onBlur={validateLastName}
                aria-invalid={isInvalid(lastNameValid)}
                aria-describedby={isInvalid(lastNameValid) ? 'invalidLastName' : undefined}
                onChange={(e) => setLastName(e.target.value)}
              />
              {isInvalid(lastNameValid) && (
                <FailureMessage id='invalidLastName'>Please enter a valid last name</FailureMessage>
              )}
            </LabelAndInputContainer>
          </FormGroup>
        </StyledFieldset>

        <LabelAndInputContainer>
          <StyledLabel htmlFor='email'>Email address</StyledLabel>
          <StyledTextInput
            required
            type='email'
            id='email'
            data-testid='email'
            name='email'
            value={email}
            aria-invalid={isInvalid(emailValid)}
            aria-describedby={isInvalid(emailValid) ? 'invalidEmail' : undefined}
            onBlur={validateEmail}
            onChange={(e) => setEmail(e.target.value)}
          />
          {isInvalid(emailValid) && (
            <FailureMessage id='invalidEmail'>Please enter a valid email address</FailureMessage>
          )}
        </LabelAndInputContainer>

        {usePhoneNumber && (
          <>
            <LabelAndInputContainer>
              <StyledLabel htmlFor='phone'>Phone number</StyledLabel>
              {phoneFieldHint && <span id='phone-hint'>{phoneFieldHint}</span>}
              <PatternFormat
                required={!!phoneRequired}
                data-testid='phone'
                format='(###) ###-####'
                mask='_'
                customInput={StyledTextInput}
                aria-invalid={isInvalid(phoneValid)}
                aria-describedby={`phone-hint ${isInvalid(phoneValid) ? 'invalidPhone' : ''}`}
                {...PhoneTextProps}
              />
              {isInvalid(phoneValid) && (
                <FailureMessage id='invalidPhone'>Please enter a valid phone number</FailureMessage>
              )}
            </LabelAndInputContainer>
          </>
        )}
        <fieldset>
          <Legend>
            I’d like to receive information from tastyfx companies about trading ideas and their products and services
            via email.
          </Legend>

          <StyledFieldset>
            <div>
              <input
                type='radio'
                name='marketingPreferencesOptIn'
                id='yes'
                data-testid='yes'
                value={`${emailConsent}`}
                checked={emailConsent}
                onChange={(e) => setEmailConsent(!!e.target.value)}
              />
              <CheckLabel htmlFor='yes'>
                {' '}
                {emailConsent ? <RadioOn aria-hidden='true' /> : <RadioOff aria-hidden='true' />} &nbsp;Yes
              </CheckLabel>
            </div>

            <div>
              <input
                type='radio'
                name='marketingPreferencesOptIn'
                id='no'
                data-testid='no'
                value={`${!emailConsent}`}
                checked={!emailConsent}
                onChange={(e) => setEmailConsent(!e.target.value)}
              />

              <CheckLabel htmlFor='no'>
                {!emailConsent ? <RadioOn aria-hidden='true' /> : <RadioOff aria-hidden='true' />}&nbsp;No
              </CheckLabel>
            </div>
          </StyledFieldset>
        </fieldset>

        <div>
          <InnerHtml richText={privacyNoticeAboveMainCTA} />
        </div>

        <SubmitButtonContainer>
          <SubmitButton data-testid='submit' type='submit' value={submitButtonText || 'Sign up'} />
          {sendingForm && <SendingFormMessage>Sending...</SendingFormMessage>}
        </SubmitButtonContainer>
      </StyledForm>
      {success && message ? (
        <SuccessMessage>{confirmationMessage || 'Sign up successful - thank you'}</SuccessMessage>
      ) : (
        <ErrorMessage>{message}</ErrorMessage>
      )}
    </ContentContainer>
  );
}

export default React.memo(ContactForm);
